import { useMemo } from 'react';
import { optionalDate } from '../optionalDate';
import { makeDateSetterFromStringSetter } from './makeDateSetterFromStringSetter';
/**
 * A state shim hook that lets you treat `useState` that returns dates
 * as a string as if it returned a date.
 *
 * Supports the full `useState` API, including the initial value and
 * passing a strongly typed function to the setter.
 *
 * @example // Extracting the setter in the usual way:
 *```ts
 * const [storageDate, setStorageDate] = useState<string|undefined>(new Date().toISOString());
 * //      ^--string|undefined
 * const [date, setDate] = useDateFromStringState(storageDate, setStorageDate);
 * //      ^----Date|undefined
 *```
 *
 * Now you can use the date setter as if it was a date setter.
 *
 * This includes basic setter usage:
 *  ```ts
 *   setDate(new Date());
 *   ```
 *
 * - And the strongly typed function setter:
 *   ```ts
 *   setDate((prevState?:Date)=>{
 *     // Do stuff here with the previous state, which was passed to
 *     // you as a date instead of a string.
 *     return new Date()
 *   });
 *   ```
 * @example // Extracting the setter using destructuring:
 * ```ts
 * const storageDateState = useState<string|undefined>(new Date().toISOString());
 * const [date, setDate] = useDateFromStringState(...storageDateState);
 * ```
 * @example // Uglified shorthand version:
 * ```ts
 * const [date, setDate] = useDateFromStringState(
 *  ...useState<string|undefined>(new Date().toISOString())
 * );
 * ```
 */
export const useDateFromStringState = (dateText, setDateText) => {
    const date = useMemo(() => optionalDate(dateText), [dateText]);
    const setDate = useMemo(() => makeDateSetterFromStringSetter(setDateText), [setDateText]);
    return [date, setDate];
};
